// Draws authored terrain layers 1/2 behind entities and layer 3 in front. No automatic terrain transitions.
// Animated water/lava/crystals, characters y-sorted together, name tags, and the light map in dark
// zones. World pixels are scaled by a whole-number zoom and snapped to screen pixels, so the art stays crisp.
import { content } from '../core/content';
import { entitySortPoint, entitySpritePosition, type PlacedEntity } from '../../../shared/src/entityGeometry';
import { contentHash } from '../core/bytes';
import { ColorMatrixFilter, Container, Graphics, Matrix, Rectangle, RenderTexture, Sprite, Texture } from 'pixi.js';
import { app } from '../app';
import { CELL, LabelFont, spriteName, type Sprites } from '../assets';
import { itemsById, mobsById } from '../api';
import { settings } from '../settings';
import { makeShadowFilter } from './shadowFilter';
import { splitEmoji, type TextPart } from '../ui/emoji';
import { CHUNK, TS, TileRules, tileHash, type TileLight } from './tiles';
import { sunShadow } from './sun';
import type { BagInfo, EntityState, HealZoneState, MeState, NpcState, ShotState, TileEntityInfo } from './types';

export interface TileSource {
  /** 1024 IDs for each ground layer 1-3 then the objects, or null if the chunk isn't loaded. */
  chunk(cx: number, cy: number): Uint16Array | null;
}

interface ShadowShape {
  projection: Container; clip: Graphics; group: Container;
  x: number; y: number; width: number; height: number;
  direction?: number; distance?: number; tip?: number;
  left: number; right: number; top: number; bottom: number;
  /** The silhouette's 4 px rows, and how far up it each is (0 at its base, 1 at its top), for the fade to the tip. */
  bands: { sprite: Sprite; frac: number }[];
}
/** A silhouette band copying a row of a caster's cell (kept in step as the caster animates). */
interface ShadowCell { source: Sprite; shadow: Sprite; band: number }
/** Silhouette rows: shadows fade from base to tip in steps this tall. */
const BAND = 4;

interface ChunkView {
  cx: number; cy: number;
  data: Uint16Array;
  ground: Sprite;
  texture: RenderTexture;
  animated: { sprite: Sprite; frames: Texture[]; lava: boolean; h: number; object: boolean }[];
  animatedLayer: Container;
  extraLayers: { ground: Sprite; texture: RenderTexture; animatedLayer: Container }[];
  tall: Container[];
  shadows: Container;
  shadowCells: ShadowCell[];
  shadowShapes: ShadowShape[];
  shadowSprite: Sprite;
  shadowTexture: RenderTexture | null;
  shadowDirty: boolean;
  shadowBounds: { left: number; top: number; right: number; bottom: number } | null;
  lights: { x: number; y: number; radius: number; color: number; flicker: boolean; seed: number }[];
}

export interface CharacterVisual {
  key: string; x: number; y: number; f: number; moving: boolean; running: boolean; anim: number; appearance: number;
  armor: number[]; flash: boolean; pose?: number; name: string; nameColor: string; marker?: string | null; markerColor?: string | null; isLocal?: boolean;
  /** What they just said aloud, over their head (alpha fades it out). */
  bubble?: { text: string; color: string; alpha: number; scale: number };
  /** A ghost running back to its corpse; on fire; inside the Rekindled shield. */
  ghost?: boolean; burning?: boolean; shielded?: boolean;
}

/** A player's corpse where it fell (world pixels). */
export interface CorpseVisual { id: number; x: number; y: number; appearance: number }

/** A laid out speech bubble: rows of text runs and emoji, x from the bubble's left, sizes in pixels. */
interface BubbleLayout { width: number; rows: { x: number; text?: string; emoji?: string }[][] }
const BUBBLE_ROWS = 4;

interface CharacterView { root: Container; shadow: Sprite; body: Sprite; layers: Sprite[]; seen: number; sun?: Container;
  /** Fire or the Rekindled shield, redrawn each frame they show; and whether they're burning (they glow at night). */
  status?: Graphics; burning?: boolean }
interface MobView { root: Container; shadow: Sprite; body: Sprite; seen: number; left: boolean; sun?: Container }

const HIT_TINT = 0xff6e6e;
/** Ghosts are pale blue and see-through; the burning flicker orange. */
const GHOST_TINT = 0xa8d8ff, GHOST_ALPHA = 0.5, BURN_TINT = 0xffbe8c;

/** The light over the world through the day (multiplied over it; white = full daylight), at times of day (0-1). */
const DAYLIGHT: [number, number][] = [
  [0.00, 0x2e3660], [0.20, 0x2e3660], [0.25, 0xa898b8], [0.29, 0xf2e2da], [0.32, 0xffffff],
  [0.68, 0xffffff], [0.72, 0xf6dcb4], [0.755, 0xd0a088], [0.80, 0x2e3660], [1.00, 0x2e3660],
];
/** The dark zones' (caves') unlit darkness. */
const CAVE_DARK = 0x16121c;

/** The day's light at a time of day, blended between DAYLIGHT's keys. */
export function daylight(time: number): number {
  const t = time - Math.floor(time);
  let i = 0;
  while (i < DAYLIGHT.length - 2 && t > DAYLIGHT[i + 1][0]) i++;
  const [t0, a] = DAYLIGHT[i], [t1, b] = DAYLIGHT[i + 1];
  const f = t1 > t0 ? Math.min(1, Math.max(0, (t - t0) / (t1 - t0))) : 0;
  const mix = (shift: number) => Math.round(((a >> shift) & 255) + (((b >> shift) & 255) - ((a >> shift) & 255)) * f);
  return (mix(16) << 16) | (mix(8) << 8) | mix(0);
}

export class WorldRenderer {
  readonly view = new Container();
  /** Ground chunks and flat things (portal swirls): under everything y-sorted. */
  private readonly groundLayer = new Container();
  private readonly background2 = new Container();
  private readonly foreground = new Container();
  /** Flat objects (rugs, flowers): over ground layers 1-2, under everything that stands. */
  private readonly flatObjects = new Container();
  private readonly flatLayer = new Container();
  /**
   * Sunlight shadows, separate from chunks so they can spill across chunk boundaries. Everything in it is a silhouette
   * drawn with max blending, so overlapping shadows never darken twice; one filter then paints the layer in the sun's
   * shadow colour and opacity.
   */
  private readonly shadowLayer = new Container();
  private readonly shadowTone = new ColorMatrixFilter();
  /** Shadows that move or change: entities', characters' and mobs' (scenery's are cached per chunk). */
  private readonly liveShadows = new Container();
  private readonly resourceShadows = new Map<string, { shape: ShadowShape; cells: ShadowCell[]; key: string }>();
  private readonly corpses = new Map<number, Sprite>();
  private readonly ghostFilter = new ColorMatrixFilter();
  private ghostVisionOn = false;
  /** Each cell texture's 4 px rows, made once. */
  private readonly bandTextures = new WeakMap<Texture, Texture[]>();
  /** Standing objects, tile entities, bags, characters and mobs, sorted by their feet (objects by their sort line). */
  readonly sorted = new Container();
  /** Effects in world space (swings, puffs) drawn above the sorted layer. */
  readonly effects = new Container();
  /** The light map (dark zones), multiplied over the world. */
  private readonly lightSprite = new Sprite();
  private lightTexture: RenderTexture | null = null;
  private readonly lightScene = new Container();
  private readonly lightBg = new Graphics();
  private readonly lightPool: Sprite[] = [];
  /** Screen-space overlays: name tags, health bars, prompts, damage numbers. */
  readonly overlay = new Container();

  private readonly rules = new TileRules();
  private readonly chunks = new Map<string, ChunkView>();
  private readonly characters = new Map<string, CharacterView>();
  private readonly mobs = new Map<number, MobView>();
  private readonly resources = new Map<string, Container>();
  private readonly tileEntities = new Map<number, Sprite[]>();
  private readonly bags = new Map<number, { root: Container; shadow: Sprite; sprite: Sprite; x: number; y: number }>();
  private readonly lightGlow: Texture;
  private readonly bakeScratch = new Container();
  /** Small per-receiver masks: only casters in front of that receiver contribute. */
  private readonly entityShadowMasks = new Map<Container, { filter: ReturnType<typeof makeShadowFilter>; texture: RenderTexture;
    version?: number; x?: number; y?: number; zoom?: number; alpha?: number; casters?: ShadowShape[] }>();
  private shadowVersion = 0;
  private readonly shadowScratch = new Container();
  private frameCounter = 0;
  private portals: { sprite: Sprite; frames: Texture[] }[] = [];
  /** Arrows and fireballs in flight (pooled sprites), and the lights the glowing ones give off. */
  private readonly shots: Sprite[] = [];
  private shotLights: { x: number; y: number; radius: number; color: number }[] = [];
  /** Healing circles, drawn on the ground each frame, and their soft light. */
  private readonly zones = new Graphics();
  private zoneLights: { x: number; y: number; radius: number; color: number }[] = [];
  /** The map's own point lights (Light placements): world pixels, radius, colour, and whether they flicker. */
  mapLights: { x: number; y: number; radius: number; color: number; flicker: boolean }[] = [];
  /** Entities' lights (a depleted one is dark), refreshed with them. */
  private resourceLights: { x: number; y: number; radius: number; color: number; flicker: boolean; seed: number }[] = [];

  zoom = 3;
  centerX = 0;
  centerY = 0;
  dark = false;
  indoors = false;
  /** The time of day (a fraction of a day: shared/src/dayNight.ts); outside dark zones it sets the light. Noon on the menus. */
  timeOfDay = 0.5;
  /** Sunlight shadows now: degrees clockwise from right, length as a % of the caster's height, how much darkness is
   * left at the tip, opacity (0: none) and colour. */
  private shadowDirection = 270;
  private shadowDistance = 90;
  private shadowTip = 0.5;
  private shadowOpacity = 0;
  private shadowColor = 0x1b1d3d;
  time = 0;
  shakeTile: { x: number; y: number } | null = null;
  highlightBag = 0;
  /** The size of text in the world, in screen pixels (set from the UI's text size). */
  labelSize = 13;

  constructor(private readonly sprites: Sprites, private readonly source: TileSource) {
    this.sorted.sortableChildren = true;
    this.groundLayer.sortableChildren = this.background2.sortableChildren = this.foreground.sortableChildren = true;
    this.view.addChild(this.groundLayer, this.background2, this.flatObjects, this.flatLayer, this.zones, this.shadowLayer, this.sorted, this.effects, this.foreground);
    this.shadowLayer.addChild(this.liveShadows);
    this.shadowLayer.filters = [this.shadowTone];
    this.lightSprite.blendMode = 'multiply';
    this.lightGlow = makeGlow();
  }

  /** Everything, in draw order: the world, its light map (screen space), then the overlays. */
  get layers(): Container[] {
    return [this.view, this.lightSprite, this.overlay];
  }

  destroy(): void {
    this.clear();
    this.lightTexture?.destroy(true);
    for (const l of this.layers) l.destroy({ children: true });
  }

  /** Drops everything (a zone change). */
  clear(): void {
    for (const state of this.entityShadowMasks.values()) { state.filter.destroy(); state.texture.destroy(true); }
    this.entityShadowMasks.clear();
    this.pending.clear();
    this.tiles.clear();
    for (const key of [...this.chunks.keys()]) this.dropChunk(key);
    for (const c of this.characters.values()) { c.root.destroy({ children: true }); c.sun?.destroy({ children: true }); }
    this.characters.clear();
    for (const m of this.mobs.values()) { m.root.destroy({ children: true }); m.sun?.destroy({ children: true }); }
    this.mobs.clear();
    for (const cells of this.tileEntities.values()) cells.forEach(c => c.destroy());
    this.tileEntities.clear();
    for (const root of this.resources.values()) root.destroy({ children: true });
    this.resources.clear();
    this.portals = [];
    for (const s of this.shots) s.destroy();
    this.shots.length = 0;
    this.shotLights = [];
    this.zones.clear(); this.zoneLights = []; this.resourceLights = [];
    for (const b of this.bags.values()) b.root.destroy({ children: true });
    this.bags.clear();
    for (const s of this.corpses.values()) s.destroy();
    this.corpses.clear();
    for (const r of this.resourceShadows.values()) r.shape.group.destroy({ children: true });
    this.resourceShadows.clear();
    this.liveShadows.removeChildren().forEach(c => c.destroy({ children: true }));
  }

  // ===========================================================================================================
  // camera
  // ===========================================================================================================

  /** Integer zoom from the window size, plus the player's offset. */
  static computeZoom(width: number, height: number, offset: number): { zoom: number; offset: number } {
    const auto = Math.max(2, Math.round(height / 270));
    const min = Math.max(1, Math.ceil(width / 2 / (36 * TS)));
    const zoom = Math.max(min, Math.min(auto + 3, auto + offset));
    return { zoom, offset: zoom - auto };
  }

  private get originX(): number { return Math.round(app.width / 2 - this.centerX * this.zoom); }
  private get originY(): number { return Math.round(app.height / 2 - this.centerY * this.zoom); }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return { x: Math.floor((x - this.centerX) * this.zoom + app.width / 2), y: Math.floor((y - this.centerY) * this.zoom + app.height / 2) };
  }

  screenToWorld(x: number, y: number): { x: number; y: number } {
    return { x: (x - app.width / 2) / this.zoom + this.centerX, y: (y - app.height / 2) / this.zoom + this.centerY };
  }

  /** A world position snapped to whole screen pixels (inside the zoomed world container). */
  private snap(v: number): number {
    return Math.floor(v * this.zoom) / this.zoom;
  }

  // ===========================================================================================================
  // chunks
  // ===========================================================================================================

  private key(cx: number, cy: number): string { return `${cx},${cy}`; }

  /** Tiles of every loaded chunk (kept current even for chunks not rebuilt yet, so neighbours join correctly). */
  private readonly tiles = new Map<string, Uint16Array>();

  /** The object on a tile, or null if its chunk isn't loaded. */
  private object(tx: number, ty: number): number | null {
    const cx = Math.floor(tx / CHUNK), cy = Math.floor(ty / CHUNK);
    const d = this.tiles.get(this.key(cx, cy));
    if (!d) return null;
    return d[3 * CHUNK * CHUNK + (ty - cy * CHUNK) * CHUNK + (tx - cx * CHUNK)] ?? 0;
  }

  private readonly pending = new Set<string>();

  /**
   * A chunk arrived, changed or unloaded: it and its neighbours (joining objects cross chunk edges) are rebuilt,
   * each once however many changes came in, a few per frame, nearest the camera first.
   */
  chunkChanged(cx: number, cy: number): void {
    const key = this.key(cx, cy), data = this.source.chunk(cx, cy);
    if (data) this.tiles.set(key, data); else this.tiles.delete(key);
    this.pending.add(key);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]])
      if (this.tiles.has(this.key(cx + dx, cy + dy))) this.pending.add(this.key(cx + dx, cy + dy));
  }

  /** Builds up to max pending chunks (all of them with max = Infinity). */
  flushChunks(max = 6): void {
    if (!this.pending.size) return;
    const ccx = this.centerX / (CHUNK * TS), ccy = this.centerY / (CHUNK * TS);
    const order = [...this.pending].map(k => k.split(',').map(Number) as [number, number])
      .sort((a, b) => Math.hypot(a[0] + 0.5 - ccx, a[1] + 0.5 - ccy) - Math.hypot(b[0] + 0.5 - ccx, b[1] + 0.5 - ccy));
    for (const [cx, cy] of order.slice(0, max)) {
      const key = this.key(cx, cy);
      this.pending.delete(key);
      const data = this.tiles.get(key);
      if (!data) this.dropChunk(key);
      else this.build(cx, cy, data);
    }
  }

  private dropChunk(key: string): void {
    const c = this.chunks.get(key);
    if (!c) return;
    this.chunks.delete(key);
    this.shadowVersion++;
    c.ground.destroy();
    c.texture.destroy(true);
    c.animatedLayer.destroy({ children: true });
    for (const l of c.extraLayers) { l.ground.destroy(); l.texture.destroy(true); l.animatedLayer.destroy({ children: true }); }
    c.shadows.destroy({ children: true });
    c.shadowSprite.destroy();
    c.shadowTexture?.destroy(true);
    for (const s of c.tall) s.destroy({ children: true });
  }

  private build(cx: number, cy: number, data: Uint16Array): void {
    const key = this.key(cx, cy);
    const old = this.chunks.get(key);
    const texture = old?.texture ?? RenderTexture.create({ width: CHUNK * TS, height: CHUNK * TS });
    if (old) {
      old.animatedLayer.destroy({ children: true });
      for (const l of old.extraLayers) { l.ground.destroy(); l.texture.destroy(true); l.animatedLayer.destroy({ children: true }); }
      old.shadows.destroy({ children: true });
      old.shadowSprite.destroy();
      old.shadowTexture?.destroy(true);
      for (const s of old.tall) s.destroy({ children: true });
      old.ground.destroy();
    }
    const view: ChunkView = {
      cx, cy, data, texture, ground: new Sprite(texture), animated: [], animatedLayer: new Container(), extraLayers: [], tall: [],
      shadows: new Container(), shadowCells: [], shadowShapes: [],
      shadowSprite: new Sprite(), shadowTexture: null, shadowDirty: true, shadowBounds: null, lights: [],
    };
    this.chunks.set(key, view);
    this.shadowVersion++;
    view.ground.position.set(cx * CHUNK * TS, cy * CHUNK * TS);
    view.animatedLayer.position.copyFrom(view.ground.position);
    view.ground.zIndex = view.animatedLayer.zIndex = -1e9;
    this.groundLayer.addChild(view.ground, view.animatedLayer);
    view.shadowSprite.blendMode = 'max';
    this.shadowLayer.addChild(view.shadowSprite);

    // Bake each authored ground layer separately. Neighbouring terrain never changes a tile's artwork.
    const bake = this.bakeScratch;
    bake.removeChildren().forEach(c => c.destroy());
    const r = this.rules, sp = this.sprites, cells = CHUNK * CHUNK;
    /** A further baked texture (and animated container) for this chunk, in a world layer. */
    const overlay = (destination: Container) => {
      const targetTexture = RenderTexture.create({ width: CHUNK * TS, height: CHUNK * TS });
      const ground = new Sprite(targetTexture), animatedLayer = new Container();
      ground.position.copyFrom(view.ground.position); animatedLayer.position.copyFrom(view.ground.position);
      ground.zIndex = animatedLayer.zIndex = -1e9;
      destination.addChild(ground, animatedLayer);
      view.extraLayers.push({ ground, texture: targetTexture, animatedLayer });
      return { targetTexture, animatedLayer };
    };
    const light = (l: TileLight | null, tx: number, ty: number) => {
      if (l) view.lights.push({ x: tx * TS + l.x, y: ty * TS + l.y, radius: l.radius, color: l.color, flicker: l.flicker, seed: tx * 7.1 + ty * 3.3 });
    };
    for (let layer = 0; layer < 3; layer++) {
      const grid = data.subarray(layer * cells, (layer + 1) * cells);
      // Empty authored overlays cost no GPU texture or draw call.
      if (layer > 0 && !grid.some(v => v !== 0)) continue;
      const { targetTexture, animatedLayer } = layer === 0 ? { targetTexture: texture, animatedLayer: view.animatedLayer }
        : overlay([this.background2, this.foreground][layer - 1]);
      for (let i = 0; i < cells; i++) {
        const g = grid[i] ?? 0, lx = i % CHUNK, ly = Math.floor(i / CHUNK), tx = cx * CHUNK + lx, ty = cy * CHUNK + ly;
        light(r.groundLight[g], tx, ty);
        const gname = r.groundSprite[g];
        if (!gname) continue;
        const frames = sp.get(gname), h = tileHash(tx, ty);
        if (r.groundAnimated[g]) {
          const s = new Sprite(frames[0]);
          s.position.set(lx * TS, ly * TS);
          animatedLayer.addChild(s);
          view.animated.push({ sprite: s, frames, lava: g === r.named.groundLava, h, object: false });
        } else add(bake, frames[h % frames.length], lx, ly);
      }
      app.pixi.renderer.render({ container: bake, target: targetTexture, clear: true });
      bake.removeChildren().forEach(c => c.destroy());
    }

    // Objects: one grid. Flat ones lie on the ground under everything that stands; the rest stand in the depth-sorted
    // scene with characters, ordered by their tile's sort line.
    const objects = data.subarray(3 * cells, 4 * cells);
    if (!objects.some(v => v !== 0)) return;
    const flat = overlay(this.flatObjects);
    for (let i = 0; i < cells; i++) {
      const o = objects[i] ?? 0;
      if (o === 0) continue;
      const lx = i % CHUNK, ly = Math.floor(i / CHUNK), tx = cx * CHUNK + lx, ty = cy * CHUNK + ly, h = tileHash(tx, ty);
      light(r.objectLight[o], tx, ty);
      const oname = r.objectSprite[o];
      if (!oname) continue;
      const size = sp.sizes.get(spriteName(oname));
      if (size && (size.w > 1 || size.h > 1)) {
        this.bigObject(view, oname, size, o, tx, ty, lx, ly, h, flat.animatedLayer);
        continue;
      }
      const frames = sp.get(oname);
      let tex: Texture;
      let animated = false;
      if (r.objectJoins[o] && frames.length >= 16) {
        let mask = 0;
        const joins = (x: number, y: number) => { const t = this.object(x, y); return t !== null && r.joins(o, t); };
        if (joins(tx, ty - 1)) mask |= 1;
        if (joins(tx + 1, ty)) mask |= 2;
        if (joins(tx, ty + 1)) mask |= 4;
        if (joins(tx - 1, ty)) mask |= 8;
        tex = frames[mask];
      } else if (r.objectAnimated[o]) {
        tex = frames[0];
        animated = true;
      } else {
        tex = frames[h % frames.length];
      }
      if (!r.objectFlat[o]) {
        const s = new Sprite(tex);
        s.position.set(tx * TS, ty * TS);
        this.standObject(view, s, o, tx, ty, tx * TS);
        this.addSceneryShadow(view, [s], tx * TS, (ty + 1) * TS, TS);
        if (animated) view.animated.push({ sprite: s, frames, lava: false, h, object: true });
      } else if (animated) {
        const s = new Sprite(tex);
        s.position.set(lx * TS, ly * TS);
        flat.animatedLayer.addChild(s);
        view.animated.push({ sprite: s, frames, lava: false, h, object: true });
      } else {
        add(bake, tex, lx, ly);
      }
    }
    app.pixi.renderer.render({ container: bake, target: flat.targetTexture, clear: true });
    bake.removeChildren().forEach(c => c.destroy());
  }

  /** A standing object joins the depth-sorted scene at its tile's sort line (SortY pixels down from the tile's top). */
  private standObject(view: ChunkView, c: Container, o: number, tx: number, ty: number, baseX: number): void {
    c.zIndex = ty * TS + this.rules.objectSortY[o];
    c.label = `object:${ty}:${tx}`;
    (c as any).tile = tx * 100000 + ty;
    (c as any).baseX = baseX;
    this.sorted.addChild(c);
    view.tall.push(c);
  }

  /**
   * An object whose sprite is bigger than a tile (a tree's canopy, a house): its frame's cells, the bottom row centred
   * on the object's tile. Standing ones are depth-sorted at their sort line; flat ones lie on the ground. The frame is a
   * variant picked by the tile.
   */
  private bigObject(view: ChunkView, name: string, size: { w: number; h: number }, o: number, tx: number, ty: number, lx: number, ly: number, h: number, flat: Container): void {
    const frames = this.sprites.frameCount.get(spriteName(name)) ?? 1;
    const cells = this.sprites.frame(name, this.rules.objectAnimated[o] ? 0 : h % frames);
    const c = new Container();
    cells.forEach((cell, i) => {
      const s = new Sprite(cell);
      s.position.set((i % size.w) * TS, Math.floor(i / size.w) * TS);
      c.addChild(s);
    });
    const left = TS / 2 - (size.w * TS) / 2, top = TS - size.h * TS;
    if (!this.rules.objectFlat[o]) {
      c.position.set(tx * TS + left, ty * TS + top);
      this.standObject(view, c, o, tx, ty, tx * TS + left);
      this.addSceneryShadow(view, c.children as Sprite[], tx * TS + left, (ty + 1) * TS, size.h * TS);
    } else {
      c.position.set(lx * TS + left, ly * TS + top);
      flat.addChild(c);
    }
  }

  /** Project the silhouette from its bottom edge along the sunlight direction.
   * Shared textures retain cutouts and nearest-neighbour sampling; no blur/filter or extra texture allocation.
   * Each chunk owns its shadows, but the separate world-space layer avoids clipping at chunk edges.
   */
  private addSceneryShadow(view: ChunkView, cells: Sprite[], left: number, bottom: number, height: number): void {
    const shape = this.makeShadowShape(cells, left, bottom, height, view.shadowCells);
    view.shadowShapes.push(shape);
    view.shadows.addChild(shape.group);
  }

  /** A caster's silhouette: its 16 px cells (a grid, bottom row at `bottom`), cut into rows that fade toward the tip. */
  private makeShadowShape(cells: Sprite[], left: number, bottom: number, height: number, out: ShadowCell[]): ShadowShape {
    const projection = new Container();
    const columns = Math.max(1, cells.length / (height / TS)), width = columns * TS;
    const clip = new Graphics(), group = new Container();
    group.addChild(projection, clip);
    projection.mask = clip;
    const shape: ShadowShape = { projection, clip, group, x: left + width / 2, y: bottom, width, height,
      left: 0, right: 0, top: 0, bottom: 0, bands: [] };
    for (const [i, source] of cells.entries()) {
      // Assemble the 16px cells before projection; source positions may be world-space.
      const x = (i % columns) * TS - width / 2, y = Math.floor(i / columns) * TS - height;
      for (let band = 0; band < TS / BAND; band++) {
        const shadow = new Sprite(this.bandOf(source.texture, band));
        shadow.blendMode = 'max';
        shadow.position.set(x, y + band * BAND);
        projection.addChild(shadow);
        out.push({ source, shadow, band });
        shape.bands.push({ sprite: shadow, frac: -(y + band * BAND + BAND / 2) / height });
      }
    }
    this.projectShadow(shape);
    return shape;
  }

  /** A 16 px cell texture's `band`th 4 px row. */
  private bandOf(texture: Texture, band: number): Texture {
    let rows = this.bandTextures.get(texture);
    if (!rows) {
      const f = texture.frame, step = f.height * BAND / TS;
      rows = Array.from({ length: TS / BAND }, (_, b) => new Texture({ source: texture.source, frame: new Rectangle(f.x, f.y + b * step, f.width, step) }));
      this.bandTextures.set(texture, rows);
    }
    return rows[band];
  }

  /** The sunlight projection from a caster's base at (x, y): up the caster becomes along the shadow, scaled by its
   * length; across the caster turns with it, so horizontal directions never collapse the silhouette. Across maps to
   * the perpendicular that points right, never left, so a silhouette is never mirrored (a character facing right
   * casts a shadow facing right, and turning doesn't flip it). */
  private sunMatrix(x: number, y: number): Matrix {
    const angle = this.shadowDirection * Math.PI / 180, length = this.shadowDistance / 100;
    const dx = Math.cos(angle), dy = Math.sin(angle);
    const flip = dy > 0 || (dy === 0 && dx > 0) ? -1 : 1; // (-dy, dx) points right for shadows cast up the screen
    return new Matrix(-dy * flip, dx * flip, -dx * length, -dy * length, x, y);
  }

  private projectShadow(shape: ShadowShape): void {
    if (shape.direction === this.shadowDirection && shape.distance === this.shadowDistance && shape.tip === this.shadowTip) return;
    shape.direction = this.shadowDirection; shape.distance = this.shadowDistance; shape.tip = this.shadowTip;
    const { projection, clip, x, y, width, height } = shape;
    const angle = this.shadowDirection * Math.PI / 180, length = this.shadowDistance / 100;
    const dx = Math.cos(angle), dy = Math.sin(angle);
    projection.setFromMatrix(this.sunMatrix(x, y));
    projection.visible = length > 0;
    // Darkest at the base, fading toward the tip.
    for (const b of shape.bands) b.sprite.alpha = 1 - (1 - this.shadowTip) * Math.max(0, Math.min(1, b.frac));
    // World-space half-plane, independent of the silhouette's rotation: never draw below its feet.
    const reach = width + height * length;
    clip.clear().rect(x - reach, y - reach, reach * 2, reach).fill(0xffffff);
    // Analytic projected bounds, clipped at the feet. Reused for culling and receiver selection.
    const halfX = Math.abs(dy) * width / 2, halfY = Math.abs(dx) * width / 2;
    shape.left = x - halfX + Math.min(0, dx * length * height);
    shape.right = x + halfX + Math.max(0, dx * length * height);
    shape.top = y - halfY + Math.min(0, dy * length * height);
    shape.bottom = y;
  }

  /** A character's or mob's sun shadow: copies of its sprites (feet at its root), projected like scenery's. */
  private castActor(view: { root: Container; sun?: Container }, sprites: Sprite[]): void {
    let sun = view.sun;
    if (!sun) { sun = view.sun = new Container(); this.liveShadows.addChild(sun); }
    const shown = sprites.filter(s => s.visible && s.texture);
    while (sun.children.length > shown.length) sun.removeChildAt(sun.children.length - 1).destroy();
    shown.forEach((source, i) => {
      const s = (sun!.children[i] as Sprite | undefined) ?? sun!.addChild(new Sprite());
      s.texture = source.texture; s.position.copyFrom(source.position); s.scale.copyFrom(source.scale);
      s.blendMode = 'max'; s.alpha = (1 + this.shadowTip) / 2; // short: the middle of a fade from base to tip
    });
    sun.setFromMatrix(this.sunMatrix(view.root.x, view.root.y));
    sun.visible = view.root.visible && this.shadowDistance > 0;
  }

  /** An entity's sun shadow, rebuilt when its art changes (depleted, another sprite), its cells kept in step. */
  private castResource(id: string, root: Container, left: number, bottom: number, height: number, key: string): void {
    let r = this.resourceShadows.get(id);
    if (!r || r.key !== key) {
      r?.shape.group.destroy({ children: true });
      const cells: ShadowCell[] = [];
      const shape = this.makeShadowShape(root.children as Sprite[], left, bottom, height, cells);
      this.liveShadows.addChild(shape.group);
      this.resourceShadows.set(id, r = { shape, cells, key });
    }
    this.projectShadow(r.shape);
    // Off-screen shadows cost nothing (each carries a clip mask).
    const shape = r.shape, tl = this.screenToWorld(0, 0), br = this.screenToWorld(app.width, app.height);
    shape.group.visible = shape.left < br.x && shape.right > tl.x && shape.top < br.y && shape.bottom > tl.y;
    if (shape.group.visible) for (const c of r.cells) { const t = this.bandOf(c.source.texture, c.band); if (c.shadow.texture !== t) c.shadow.texture = t; }
  }

  /** Static silhouettes pay their stencil/batch cost only when their projection or animation changes. */
  private cacheShadows(c: ChunkView): void {
    if (!c.shadowShapes.length) return;
    c.shadowBounds ??= {
      left: Math.floor(Math.min(...c.shadowShapes.map(s => s.left))),
      top: Math.floor(Math.min(...c.shadowShapes.map(s => s.top))),
      right: Math.ceil(Math.max(...c.shadowShapes.map(s => s.right))),
      bottom: Math.ceil(Math.max(...c.shadowShapes.map(s => s.bottom))),
    };
    const { left, top, right, bottom } = c.shadowBounds;
    const tl = this.screenToWorld(0, 0), br = this.screenToWorld(app.width, app.height);
    c.shadowSprite.visible = this.shadowDistance > 0 && left < br.x && right > tl.x && top < br.y && bottom > tl.y;
    if (!c.shadowDirty || !c.shadowSprite.visible || !this.shadowLayer.visible) return;
    const width = Math.max(1, right - left), height = Math.max(1, bottom - top);
    if (!c.shadowTexture || c.shadowTexture.width !== width || c.shadowTexture.height !== height) {
      c.shadowTexture?.destroy(true);
      c.shadowTexture = RenderTexture.create({ width, height, resolution: 1 });
      c.shadowTexture.source.scaleMode = 'nearest';
      c.shadowSprite.texture = c.shadowTexture;
    }
    c.shadowSprite.position.set(left, top);
    app.pixi.renderer.render({ container: c.shadows, target: c.shadowTexture, clear: true,
      transform: new Matrix(1, 0, 0, 1, -left, -top) });
    c.shadowDirty = false;
  }

  private renderEntityShadows(): void {
    const receivers: { root: Container; y: number }[] = [];
    for (const v of this.characters.values()) receivers.push({ root: v.root, y: v.root.y });
    for (const v of this.mobs.values()) receivers.push({ root: v.root, y: v.root.y });
    for (const v of this.bags.values()) receivers.push({ root: v.root, y: v.root.y });
    for (const cells of this.tileEntities.values()) for (const s of cells)
      if (!(s as any).portal) receivers.push({ root: s, y: s.zIndex });
    for (const s of this.shots) receivers.push({ root: s, y: s.y + 6 });
    const live = new Set(receivers.map(r => r.root));
    for (const [root, state] of this.entityShadowMasks) if (!live.has(root)) {
      state.filter.destroy(); state.texture.destroy(true); this.entityShadowMasks.delete(root);
    }
    const enabled = settings.shadows && settings.entityShadows && this.shadowLayer.visible && this.shadowDistance > 0;
    if (!enabled) {
      for (const { root } of receivers) root.filters = null;
      return;
    }
    const tl = this.screenToWorld(0, 0), br = this.screenToWorld(app.width, app.height);
    const candidates: ShadowShape[] = [];
    for (const shape of [...[...this.chunks.values()].flatMap(c => c.shadowShapes), ...[...this.resourceShadows.values()].map(r => r.shape)])
      if (shape.left < br.x && shape.right > tl.x && shape.top < br.y && shape.bottom > tl.y) candidates.push(shape);
    const entries = receivers.filter(receiver => receiver.root.visible)
      .map(receiver => ({ ...receiver, bounds: receiver.root.getBounds() }));
    for (const { root } of receivers) if (!root.visible) root.filters = null;
    for (const { root, y, bounds } of entries) {
      root.filters = null;
      const left = Math.max(0, Math.floor(bounds.minX)), top = Math.max(0, Math.floor(bounds.minY));
      const right = Math.min(app.width, Math.ceil(bounds.maxX)), bottom = Math.min(app.height, Math.ceil(bounds.maxY));
      if (right <= left || bottom <= top) continue;
      const worldLeft = (left - this.originX) / this.zoom, worldRight = (right - this.originX) / this.zoom;
      const worldTop = (top - this.originY) / this.zoom, worldBottom = (bottom - this.originY) / this.zoom;
      const eligible = candidates.filter(c => y < c.y && c.left < worldRight && c.right > worldLeft &&
        c.top < worldBottom && c.bottom > worldTop);
      if (!eligible.length) continue;
      const width = right - left, height = bottom - top;
      let state = this.entityShadowMasks.get(root);
      if (!state) {
        state = { filter: makeShadowFilter(), texture: RenderTexture.create({ width, height, resolution: 1 }) };
        this.entityShadowMasks.set(root, state);
      } else if (state.texture.width !== width || state.texture.height !== height) {
        state.texture.destroy(true);
        state.texture = RenderTexture.create({ width, height, resolution: 1 });
        state.version = undefined;
      }
      state.texture.source.scaleMode = 'nearest';
      state.filter.resources.uShadowMask = state.texture.source;
      state.filter.resources.shadowUniforms.uniforms.uMaskSize = [width, height];
      state.filter.resources.shadowUniforms.uniforms.uMaskOrigin = [left, top];
      state.filter.resources.shadowUniforms.uniforms.uShadowColor = [((this.shadowColor >> 16) & 255) / 255, ((this.shadowColor >> 8) & 255) / 255, (this.shadowColor & 255) / 255];
      const maskX = this.originX - left, maskY = this.originY - top;
      if (state.version === this.shadowVersion && state.x === maskX && state.y === maskY &&
        state.zoom === this.zoom && state.alpha === this.shadowOpacity &&
        state.casters?.length === eligible.length && state.casters.every((s, i) => s === eligible[i])) {
        root.filters = [state.filter];
        continue;
      }
      // Draw only selected casters, rather than traversing every loaded chunk per receiver.
      const parents = eligible.map(shape => shape.group.parent!);
      try {
        for (const shape of eligible) this.shadowScratch.addChild(shape.group);
        this.shadowScratch.alpha = this.shadowOpacity;
        app.pixi.renderer.render({ container: this.shadowScratch, target: state.texture, clear: true,
          transform: new Matrix(this.zoom, 0, 0, this.zoom, this.originX - left, this.originY - top) });
      } finally {
        eligible.forEach((shape, i) => parents[i].addChild(shape.group));
      }
      state.version = this.shadowVersion; state.x = maskX; state.y = maskY;
      state.zoom = this.zoom; state.alpha = this.shadowOpacity; state.casters = eligible;
      root.filters = [state.filter];
    }
  }

  // ===========================================================================================================
  // things in the world
  // ===========================================================================================================

  /** Functional scenery shares the character depth pass; anchor, collision and sort point are independent. */
  setResources(entities: PlacedEntity[], time: number): void {
    const seen = new Set<string>();
    this.resourceLights = [];
    for (const e of [...entities].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0)) {
      const d = content().entity(e.definition);
      if (!d) continue;
      const name = e.depleted ? d.gather?.depletedSprite ?? d.sprite : d.sprite;
      if (!name) continue;
      seen.add(e.id);
      let root = this.resources.get(e.id);
      if (!root) { root = new Container(); root.label = `resource:${e.id}`; this.resources.set(e.id, root); this.sorted.addChild(root); }
      const size = this.sprites.sizes.get(spriteName(name)) ?? { w: 1, h: 1 };
      const frame = this.sprites.frame(name, (d.animated ? Math.floor(time * 4) : contentHash(e.id)) % (this.sprites.frameCount.get(spriteName(name)) ?? 1));
      while (root.children.length > frame.length) root.removeChildAt(root.children.length - 1).destroy();
      frame.forEach((texture, i) => {
        const cell = root!.children[i] as Sprite | undefined;
        const s = cell ?? root!.addChild(new Sprite(texture)); s.texture = texture;
        s.position.set((i % size.w) * TS, Math.floor(i / size.w) * TS);
      });
      const p = entitySpritePosition(e, d), sort = entitySortPoint(e, d);
      root.position.set(p.x, p.y); root.zIndex = sort.y;
      if (d.lightRadius > 0 && !e.depleted) this.resourceLights.push({ x: e.x * TS + e.offsetX + d.lightX, y: e.y * TS + e.offsetY + d.lightY,
        radius: d.lightRadius, color: /^#[0-9a-f]{6}$/i.test(d.lightColor) ? parseInt(d.lightColor.slice(1), 16) : 0xffffff, flicker: d.animated, seed: e.x * 7.1 + e.y * 3.3 });
      // Things that stand in the way (trees, rocks, veins; their stumps and rubble too) cast sun shadows from their base.
      if (d.collision.width > 0) this.castResource(e.id, root, p.x, p.y + size.h * TS, size.h * TS, `${name}:${frame.length}`);
      if (this.shakeTile?.x === e.x && this.shakeTile.y === e.y) root.x += Math.round(Math.sin(time * 45) * 0.7);
    }
    for (const [id, root] of this.resources) if (!seen.has(id)) {
      root.destroy({ children: true }); this.resources.delete(id);
      this.resourceShadows.get(id)?.shape.group.destroy({ children: true }); this.resourceShadows.delete(id);
    }
  }

  /** Tile entities (workbench cells y-sorted, portal swirls flat) and loot bags, whenever they change. */
  setObjects(tileEntities: TileEntityInfo[], bags: BagInfo[]): void {
    const tents = new Set(tileEntities.map(t => t.id));
    for (const [id, cells] of this.tileEntities) if (!tents.has(id)) { cells.forEach(c => c.destroy()); this.tileEntities.delete(id); }
    for (const t of tileEntities) {
      if (this.tileEntities.has(t.id)) continue;
      const cells: Sprite[] = [];
      if (t.type === 3) { // portal: flat and animated, unless hidden (a painted door or stairs)
        if (!t.hidden) {
          const s = new Sprite(this.sprites.get('portal')[0]);
          s.position.set(t.x * TS, t.y * TS);
          (s as any).portal = true;
          this.flatLayer.addChild(s);
          cells.push(s);
        }
      } // Invisible station points have no artwork; NPCs are drawn as characters.
      this.tileEntities.set(t.id, cells);
    }
    this.portals = [...this.tileEntities.values()].flat().filter(s => (s as any).portal).map(sprite => ({ sprite, frames: this.sprites.get('portal') }));

    const ids = new Set(bags.map(b => b.id));
    for (const [id, b] of this.bags) if (!ids.has(id)) { b.root.destroy({ children: true }); this.bags.delete(id); }
    for (const b of bags) {
      let v = this.bags.get(b.id);
      if (!v) {
        const root = new Container();
        const shadow = new Sprite(this.sprites.uiCell('shadow'));
        shadow.alpha = 0.8;
        shadow.position.set(-8, -14);
        const sprite = new Sprite(this.sprites.get('loot_bag')[0]);
        sprite.position.set(-8, -15);
        root.addChild(shadow, sprite);
        this.sorted.addChild(root);
        v = { root, shadow, sprite, x: b.x, y: b.y };
        this.bags.set(b.id, v);
      }
      v.root.position.set(b.x, b.y);
      v.root.label = `bag:${b.id}`;
      v.root.zIndex = b.y;
    }
  }

  /** Players (local, remote, NPCs) this frame: anything not listed is removed. */
  setCharacters(list: CharacterVisual[]): void {
    const frame = ++this.frameCounter;
    for (const c of list) {
      let v = this.characters.get(c.key);
      if (!v) {
        const root = new Container();
        const shadow = new Sprite(this.sprites.uiCell('shadow'));
        const body = new Sprite();
        root.addChild(shadow, body);
        this.sorted.addChild(root);
        v = { root, shadow, body, layers: [], seen: 0 };
        this.characters.set(c.key, v);
      }
      v.seen = frame;
      const x = this.snap(c.x), y = this.snap(c.y);
      v.root.position.set(x, y);
      v.root.label = `actor:${c.key}`;
      v.root.zIndex = c.y;
      v.shadow.position.set(-8, -14);
      let anim = 0;
      if (c.moving) anim = [1, 0, 2, 0][Math.floor(c.anim * (c.running ? 10 : 6.5)) % 4];
      if (c.pose !== undefined) anim = c.pose; // a frame the action asks for (stepping into a swing)
      v.body.texture = this.sprites.character(c.appearance, c.f, anim);
      v.body.position.set(-8, -15);
      const burnFlicker = c.burning && Math.floor(this.time * 8 + c.key.length * 3) % 3 !== 0;
      v.body.tint = c.flash ? HIT_TINT : c.ghost ? GHOST_TINT : burnFlicker ? BURN_TINT : 0xffffff;
      v.body.alpha = c.ghost ? GHOST_ALPHA : 1;
      // Worn armour over the body: feet, legs, chest, head.
      let n = 0;
      for (const slot of [3, 2, 1, 0]) {
        const item = itemsById.get(c.armor[slot] ?? 0);
        if (!item || item.armor < 0) continue;
        const layer = this.sprites.armorLayer(`${item.look}_${['head', 'chest', 'legs', 'feet'][item.armor]}`, c.f, anim);
        if (!layer) continue;
        let s = v.layers[n];
        if (!s) { s = new Sprite(); v.layers.push(s); v.root.addChild(s); }
        s.texture = layer;
        s.position.set(-8, -15);
        s.tint = v.body.tint;
        s.alpha = v.body.alpha;
        s.visible = true;
        n++;
      }
      for (let i = n; i < v.layers.length; i++) v.layers[i].visible = false;
      // Ghosts cast no shadow; the living do, sun and blob.
      v.shadow.visible = settings.shadows && !c.ghost;
      if (c.ghost) { if (v.sun) v.sun.visible = false; }
      else this.castActor(v, [v.body, ...v.layers]);
      this.drawStatus(v, c);
    }
    for (const [key, v] of this.characters) if (v.seen !== frame) { v.root.destroy({ children: true }); v.sun?.destroy({ children: true }); this.characters.delete(key); }
    this.drawTags(list);
  }

  /** Flames licking up a burning character (with embers rising), and the Rekindled shield: a pulsing golden bubble. */
  private drawStatus(v: CharacterView, c: CharacterVisual): void {
    v.burning = !!c.burning;
    if (!c.burning && !c.shielded) { if (v.status) v.status.visible = false; return; }
    if (!v.status) { v.status = new Graphics(); v.root.addChild(v.status); }
    const g = v.status.clear(), t = this.time + c.key.length * 0.37;
    v.status.visible = true;
    if (c.burning) {
      // Tongues of fire at the feet and up the body, each wavering on its own beat.
      for (const [bx, by, size] of [[-5, 0, 1], [-1, 0, 1.2], [3, 0, 1], [-3, -6, 0.75], [2, -7, 0.7]] as const) {
        const phase = t * 7 + bx * 1.3 + by, h = Math.round((4 + 3 * Math.abs(Math.sin(phase))) * size), w = Math.round(2 * size) + 1;
        const lean = Math.round(Math.sin(phase * 0.7));
        g.poly([bx - w, by, bx + lean, by - h, bx + w, by]).fill({ color: 0xff6a1a, alpha: 0.9 });
        g.poly([bx - w + 1, by, bx + lean, by - Math.round(h * 0.6), bx + w - 1, by]).fill({ color: 0xffd24a, alpha: 0.95 });
      }
      for (let i = 0; i < 3; i++) {
        const k = (t * 1.4 + i / 3) % 1;
        g.rect(Math.round(Math.sin(i * 2.1 + t * 3) * 5), Math.round(-4 - k * 14), 1, 1).fill({ color: 0xffe08a, alpha: 1 - k });
      }
    }
    if (c.shielded) {
      // An absorb bubble around the whole body: a soft gold fill, a bright rim, and a highlight sweeping round it.
      const r = 11 + Math.sin(t * 4) * 0.6, cy = -7, a = t * 2.2;
      g.circle(0, cy, r).fill({ color: 0xffe08a, alpha: 0.16 }).stroke({ width: 1, color: 0xffd56a, alpha: 0.8 });
      g.moveTo(Math.cos(a) * (r - 2), cy + Math.sin(a) * (r - 2)).arc(0, cy, r - 2, a, a + 1.1).stroke({ width: 1, color: 0xffffff, alpha: 0.65 });
    }
  }

  /** Players' corpses: lying where they fell, greyed, under anyone standing near them. */
  setCorpses(list: CorpseVisual[]): void {
    const seen = new Set<number>();
    for (const c of list) {
      seen.add(c.id);
      let s = this.corpses.get(c.id);
      if (!s) {
        s = new Sprite(); s.anchor.set(0.5); s.label = `corpse:${c.id}`; s.rotation = -Math.PI / 2; s.tint = 0x8a8a8a;
        this.sorted.addChild(s); this.corpses.set(c.id, s);
      }
      s.texture = this.sprites.character(c.appearance, 0, 0);
      s.position.set(Math.round(c.x), Math.round(c.y - 4));
      s.zIndex = c.y - 8;
    }
    for (const [id, s] of this.corpses) if (!seen.has(id)) { s.destroy(); this.corpses.delete(id); }
  }

  /** A ghost sees a drained, cold world. */
  set ghostVision(on: boolean) {
    if (on === this.ghostVisionOn) return;
    this.ghostVisionOn = on;
    if (on) { this.ghostFilter.reset(); this.ghostFilter.desaturate(); this.ghostFilter.tint(0xc8dcff, true); }
    this.view.filters = on ? [this.ghostFilter] : null;
  }

  setMobs(list: EntityState[]): void {
    const frame = this.frameCounter;
    for (const m of list) {
      let v = this.mobs.get(m.id);
      const def = mobsById.get(m.type ?? 0);
      if (!v) {
        const root = new Container();
        const shadow = new Sprite(this.sprites.uiCell('shadow'));
        shadow.alpha = 0.8;
        const body = new Sprite();
        root.addChild(shadow, body);
        this.sorted.addChild(root);
        v = { root, shadow, body, seen: 0, left: false };
        this.mobs.set(m.id, v);
      }
      v.seen = frame;
      v.root.position.set(this.snap(m.x), this.snap(m.y));
      v.root.label = `mob:${m.id}`;
      v.root.zIndex = m.y;
      const frames = this.sprites.get(def?.sprite ?? 'slime');
      const pick = (i: number) => frames[Math.min(i, frames.length - 1)];
      // Frames 1-2 rest (a slow breathe, phase varying per mob). A 3-frame sprite hops: its third frame is the
      // stretched hop. Longer sprites walk: frames 3+ cycle while moving, drawn facing right and mirrored when the
      // mob heads left (up/down keep the last side).
      const walker = frames.length > 3;
      if (m.f === 1) v.left = true; else if (m.f === 2) v.left = false; // Facing.Left / Facing.Right
      v.body.texture = !m.moving ? pick(Math.floor(this.time * 1.6 + m.id * 0.37) % 2)
        : walker ? pick(2 + Math.floor(this.time * 8 + m.id * 0.37) % (frames.length - 2)) : pick(2);
      const mirror = walker && v.left;
      v.body.scale.x = mirror ? -1 : 1;
      v.body.position.set(mirror ? 8 : -8, -15);
      v.body.tint = m.flash ? HIT_TINT : parseInt((def?.tint ?? '#ffffff').slice(1), 16);
      const sw = m.moving ? 10 : 13;
      v.shadow.width = sw;
      v.shadow.height = 16;
      v.shadow.position.set(-sw / 2, -14);
      this.castActor(v, [v.body]);
    }
    for (const [id, v] of this.mobs) if (v.seen !== frame) { v.root.destroy({ children: true }); v.sun?.destroy({ children: true }); this.mobs.delete(id); }
  }

  /**
   * Arrows and fireballs in flight: each sprite turned to where it flies (drawn pointing up-right), animated, sorted with
   * what it passes (it flies at chest height, 6 px above the feet), lit if it glows. Before update(), for its lights.
   */
  setShots(list: ShotState[]): void {
    list.forEach((p, i) => {
      let s = this.shots[i];
      if (!s) {
        s = new Sprite();
        s.anchor.set(0.5);
        this.sorted.addChild(s);
        this.shots.push(s);
      }
      const frames = this.sprites.get(p.sprite);
      s.visible = true;
      s.texture = frames[Math.floor(p.age * 12) % frames.length];
      s.position.set(p.x, p.y);
      s.rotation = p.angle + Math.PI / 4;
      s.zIndex = p.y + 6;
    });
    for (let i = list.length; i < this.shots.length; i++) this.shots[i].visible = false;
    this.shotLights = list.filter(p => p.lightRadius > 0)
      .map(p => ({ x: p.x, y: p.y, radius: p.lightRadius, color: parseInt(p.lightColor.slice(1), 16) || 0xffffff }));
  }

  /** Healing circles: a glowing ring with a soft fill, a ripple running outward, fading at the end of their time. */
  setHealZones(list: HealZoneState[]): void {
    const g = this.zones;
    g.clear();
    for (const z of list) {
      const color = parseInt(z.color.slice(1), 16) || 0x7fd99a, fade = Math.min(1, (1 - z.life) * 8, z.life * 20);
      g.circle(z.x, z.y, z.radius).fill({ color, alpha: 0.16 * fade }).stroke({ width: 1.5, color, alpha: 0.8 * fade });
      const ripple = (this.time * 0.8) % 1;
      g.circle(z.x, z.y, z.radius * ripple).stroke({ width: 1, color, alpha: 0.45 * (1 - ripple) * fade });
    }
    this.zoneLights = list.map(z => ({ x: z.x, y: z.y, radius: z.radius * 1.5, color: parseInt(z.color.slice(1), 16) || 0x7fd99a }));
  }

  // ===========================================================================================================
  // per frame
  // ===========================================================================================================

  update(dt: number): void {
    this.sorted.sortChildren();
    this.sorted.children.sort((a, b) => a.zIndex - b.zIndex || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
    this.time += dt;
    this.flushChunks();
    const z = this.zoom;
    this.view.scale.set(z);
    this.view.position.set(this.originX, this.originY);
    this.shadowLayer.visible = settings.shadows;
    for (const v of this.characters.values()) v.shadow.visible = settings.shadows;
    for (const v of this.mobs.values()) v.shadow.visible = settings.shadows;
    for (const v of this.bags.values()) v.shadow.visible = settings.shadows;
    // Sunlight: the shadow swings, stretches, fades and tints with the time of day (the admin sliders can pin where it
    // points and how long it is). None indoors, in dark zones or at night.
    const sun = sunShadow(this.timeOfDay);
    if (!settings.shadowsFollowSun) { sun.direction = settings.shadowDirection; sun.distance = settings.shadowDistance; }
    this.shadowDirection = sun.direction; this.shadowDistance = sun.distance; this.shadowTip = sun.tip;
    this.shadowOpacity = this.dark || this.indoors || !settings.shadows ? 0 : sun.opacity;
    this.shadowColor = sun.color;
    this.shadowLayer.visible = this.shadowOpacity > 0;
    // Every silhouette becomes the shadow colour at the shadow's opacity (offsets carry the colour; alpha scales).
    this.shadowTone.matrix = [0, 0, 0, 0, ((sun.color >> 16) & 255) / 255, 0, 0, 0, 0, ((sun.color >> 8) & 255) / 255,
      0, 0, 0, 0, (sun.color & 255) / 255, 0, 0, 0, this.shadowOpacity, 0];

    // Only chunks on screen are drawn.
    const tl = this.screenToWorld(0, 0), br = this.screenToWorld(app.width, app.height);
    const waterFrame = Math.floor(this.time * 3) % 4, crystalFrame = Math.floor(this.time * 4);
    for (const c of this.chunks.values()) {
      const x0 = c.cx * CHUNK * TS, y0 = c.cy * CHUNK * TS;
      const visible = x0 < br.x + TS && x0 + CHUNK * TS > tl.x - TS && y0 < br.y + TS * 2 && y0 + CHUNK * TS > tl.y - TS * 2;
      c.ground.visible = c.animatedLayer.visible = visible;
      for (const l of c.extraLayers) l.ground.visible = l.animatedLayer.visible = visible;
      for (const a of c.animated) {
        if (!visible && !a.object) continue;
        const f = a.object ? (crystalFrame + a.h % 3) % a.frames.length : (waterFrame + (a.lava ? a.h % 2 : 0)) % a.frames.length;
        a.sprite.texture = a.frames[f];
      }
      for (const { source, shadow, band } of c.shadowCells) {
        const t = this.bandOf(source.texture, band);
        if (shadow.texture !== t) { shadow.texture = t; c.shadowDirty = true; this.shadowVersion++; }
      }
    }
    for (const p of this.portals) p.sprite.texture = p.frames[Math.floor(this.time * 8) % p.frames.length];

    // The object being harvested jolts at each hit (about 2.5 per second).
    for (const c of this.chunks.values()) {
      for (const s of c.tall) {
        // Local geometry is static; only the object's position/alpha changes during harvesting.
        const bounds = (s as any).visualBounds ?? ((s as any).visualBounds = s.getLocalBounds().clone());
        s.visible = s.x + bounds.minX < br.x && s.x + bounds.maxX > tl.x &&
          s.y + bounds.minY < br.y && s.y + bounds.maxY > tl.y;
        if (!s.visible) continue;
        const tile = (s as any).tile as number;
        const tx = Math.floor(tile / 100000), ty = tile % 100000;
        let shake = 0;
        if (this.shakeTile && this.shakeTile.x === tx && this.shakeTile.y === ty) {
          const phase = (this.time * 2.5) % 1;
          shake = phase < 0.15 ? Math.round(Math.sin((phase / 0.15) * Math.PI * 3) * z) / z : 0;
        }
        s.x = (s as any).baseX + shake;
        // Authored draw layers are fixed: foreground never auto-fades behind the player.
      }
    }
    for (const [id, b] of this.bags) {
      const lifted = id === this.highlightBag;
      b.sprite.y = lifted ? -16 : -15;
      b.sprite.tint = lifted ? 0xfff0d2 : 0xffffff;
    }
    // Reproject without rebuilding terrain as the sun moves (whole degrees/percent) or the admin sliders are dragged.
    for (const c of this.chunks.values()) {
      for (const shape of c.shadowShapes) {
        if (shape.direction !== this.shadowDirection || shape.distance !== this.shadowDistance || shape.tip !== this.shadowTip) {
          c.shadowDirty = true; c.shadowBounds = null; this.shadowVersion++;
        }
        this.projectShadow(shape);
      }
      this.cacheShadows(c);
    }
    this.renderEntityShadows();
    this.renderLight();
  }

  // ===========================================================================================================
  // labels
  // ===========================================================================================================

  private readonly tagPool: Sprite[] = [];
  private tagCount = 0;
  private readonly bars = new Graphics();

  private tag(texture: Texture, x: number, y: number, alpha = 1, size = 0): Sprite {
    let s = this.tagPool[this.tagCount];
    if (!s) {
      s = new Sprite();
      this.tagPool.push(s);
      this.overlay.addChild(s);
    }
    this.tagCount++;
    s.texture = texture;
    if (size) { s.width = size; s.height = size; } else s.scale.set(1);
    s.position.set(Math.round(x), Math.round(y));
    s.alpha = alpha;
    s.visible = true;
    return s;
  }

  /** A label centred on x with its text's bottom at y (the texture's outline padding hangs outside). */
  private label(text: string, color: string, x: number, y: number, size: number, weight = 500, alpha = 1): Sprite {
    const tex = this.sprites.font.texture(text, color, size, weight), pad = LabelFont.pad(size);
    return this.tag(tex, x - tex.width / 2, y - tex.height + pad, alpha);
  }

  /** Name tags (after lighting, so they stay readable), quest markers, mob levels and health bars. */
  private drawTags(chars: CharacterVisual[]): void {
    this.tagCount = 0;
    if (!this.bars.parent) this.overlay.addChildAt(this.bars, 0);
    this.bars.clear();
    const size = this.labelSize, z = this.zoom;
    for (const c of chars) {
      const feet = this.worldToScreen(c.x, c.y);
      const top = feet.y - 17 * z;
      this.label(c.name, c.nameColor, feet.x, top, size, c.isLocal ? 500 : 400);
      if (c.marker) {
        const bob = Math.round(Math.sin(this.time * 3) * size * 0.15);
        this.label(c.marker, c.markerColor ?? '#ffd246', feet.x, top - size * 1.2 + bob, Math.round(size * 1.9), 600);
      }
      if (c.bubble) this.bubble(c.bubble, feet.x, top - size * (c.marker ? 3 : 1.3));
    }
  }

  private readonly bubbleLayouts = new Map<string, BubbleLayout>();

  /** Wraps a message by word into at most BUBBLE_ROWS rows (the last ending "..." if cut), emoji inline. */
  private layoutBubble(text: string, size: number): BubbleLayout {
    const key = `${size}|${text}`;
    const cached = this.bubbleLayouts.get(key);
    if (cached) return cached;
    const font = this.sprites.font, maxWidth = size * 15, emoji = Math.round(size * 1.25), space = font.measure(' ', size, 400);
    const rows: { x: number; text?: string; emoji?: string }[][] = [[]];
    let x = 0, cut = false;
    const fits = (w: number) => x === 0 || x + w <= maxWidth;
    const newRow = () => { if (rows.length === BUBBLE_ROWS) { cut = true; return false; } rows.push([]); x = 0; return true; };
    const words = splitEmoji(text).flatMap((p): TextPart[] => typeof p === 'string' ? p.split(/\s+/).filter(Boolean) : [p]);
    for (const word of words) {
      if (cut) break;
      const w = typeof word === 'string' ? font.measure(word, size, 400) : emoji;
      if (!fits(w) && !newRow()) break;
      const row = rows[rows.length - 1], last = row[row.length - 1];
      if (typeof word !== 'string') row.push({ x: x ? x + space : 0, emoji: word.emoji });
      else if (last?.text !== undefined) last.text += ' ' + word;
      else row.push({ x: x ? x + space : 0, text: word });
      x += (x ? space : 0) + w;
    }
    if (cut) {
      const row = rows[rows.length - 1], last = row[row.length - 1];
      if (last?.text !== undefined) last.text += '...'; else row.push({ x: x + space, text: '...' });
    }
    // Width from what was placed (a run's width is measured whole, as drawn).
    let width = 0;
    for (const row of rows) for (const run of row) width = Math.max(width, run.x + (run.text !== undefined ? font.measure(run.text, size, 400) : emoji));
    const layout = { width: Math.ceil(Math.min(width, maxWidth * 1.2)), rows };
    if (this.bubbleLayouts.size > 200) this.bubbleLayouts.clear();
    this.bubbleLayouts.set(key, layout);
    return layout;
  }

  /** A speech bubble centred on x with its tail's tip at y. */
  private bubble(b: { text: string; color: string; alpha: number; scale: number }, x: number, y: number): void {
    const size = Math.round(this.labelSize * 0.93 * b.scale), layout = this.layoutBubble(b.text, size);
    const lineH = Math.ceil(size * 1.25), padX = Math.round(size * 0.5), padY = Math.round(size * 0.3), tail = Math.round(size * 0.45);
    const w = layout.width + padX * 2, h = layout.rows.length * lineH + padY * 2;
    const left = Math.round(x - w / 2), top = Math.round(y - tail - h), r = Math.round(size * 0.5);
    this.bars.roundRect(left, top, w, h, r).fill({ color: 0x0c0a0e, alpha: 0.78 * b.alpha })
      .roundRect(left, top, w, h, r).stroke({ color: 0xc99a50, alpha: 0.55 * b.alpha, width: 1 })
      .poly([x - tail, top + h, x + tail, top + h, x, top + h + tail]).fill({ color: 0x0c0a0e, alpha: 0.78 * b.alpha });
    const pad = LabelFont.pad(size), emoji = Math.round(size * 1.25);
    layout.rows.forEach((row, i) => {
      const rowTop = top + padY + i * lineH;
      for (const run of row) {
        if (run.emoji) this.tag(this.sprites.emoji(run.emoji), left + padX + run.x, rowTop + (lineH - emoji) / 2, b.alpha, emoji);
        else this.tag(this.sprites.font.texture(run.text!, b.color, size, 400), left + padX + run.x - pad, rowTop - pad, b.alpha);
      }
    });
  }

  /** Mob level tags and health bars (called after setMobs). */
  drawMobTags(mobs: EntityState[]): void {
    const size = Math.round(this.labelSize * 0.86), z = this.zoom;
    for (const m of mobs) {
      const feet = this.worldToScreen(m.x, m.y);
      if (m.level > 0) this.label(`Lv ${m.level}`, m.levelColor ?? '#fff', feet.x, feet.y - 17 * z, size, 500);
      if (m.hp >= 255) continue;
      const w = 14 * z, h = Math.max(2, z), x = feet.x - w / 2, y = feet.y - 15 * z;
      this.bars.rect(x - 1, y - 1, w + 2, h + 2).fill(0x0f0a0c);
      this.bars.rect(x, y, w, h).fill(0x461e1e);
      this.bars.rect(x, y, Math.floor(w * m.hp / 255), h).fill(0xdc3c32);
    }
  }

  /** A floating label anchored at a world point (E prompts), on a dark plate. */
  prompt(text: string, worldX: number, worldY: number, color: string, below = false, bob = true): void {
    const size = this.labelSize, tex = this.sprites.font.texture(text, color, size, 500, undefined, true), pad = LabelFont.pad(size);
    const at = this.worldToScreen(worldX, worldY + (below ? size / this.zoom : 0));
    const b = bob ? Math.round(Math.sin(app.time * 4) * this.zoom * 0.5) : 0;
    this.tag(tex, Math.round(at.x - tex.width / 2), at.y - tex.height + pad + b);
  }

  /** The harvest progress bar over a tile. */
  progressBar(worldX: number, worldY: number, progress: number): void {
    const z = this.zoom, top = this.worldToScreen(worldX, worldY);
    const w = (TS - 2) * z, h = 3 * z;
    this.bars.rect(top.x - z, top.y - z, w + 2 * z, h + 2 * z).fill(0x140e10);
    this.bars.rect(top.x, top.y, w, h).fill(0x3c2c28);
    const lerp = (a: number, b: number) => Math.round(a + (b - a) * progress);
    const fill = (lerp(230, 150) << 16) | (lerp(140, 220) << 8) | lerp(50, 90);
    this.bars.rect(top.x, top.y, Math.floor(w * progress), h).fill(fill);
    this.bars.rect(top.x, top.y, Math.floor(w * progress), z).fill({ color: 0xffffff, alpha: 0.35 });
  }

  /** Floating text (damage numbers, exp): screen-space, drawn by the effects code. */
  text(text: string, color: string, sx: number, sy: number, alpha: number): void {
    const size = Math.round(this.labelSize * 1.1);
    const tex = this.sprites.font.texture(text, color, size, 600);
    this.tag(tex, sx - tex.width / 2, sy, alpha);
  }

  get barsGraphics(): Graphics { return this.bars; }

  /** Hides pooled labels not used this frame (call after all labels are drawn). */
  finishLabels(): void {
    for (let i = this.tagCount; i < this.tagPool.length; i++) this.tagPool[i].visible = false;
  }

  // ===========================================================================================================
  // light
  // ===========================================================================================================

  /**
   * The light map, multiplied over the world: the ambient light (a dark zone's darkness, or the day's light at this time
   * of day), plus every light in view added on top (tiles with a light, glowing stations, portals, and a little around
   * players so nobody is lost in the dark). Skipped in full daylight.
   */
  private renderLight(): void {
    const ambient = this.indoors ? 0xffffff : this.dark ? CAVE_DARK : daylight(this.timeOfDay);
    this.lightSprite.visible = ambient !== 0xffffff;
    if (!this.lightSprite.visible) return;
    // How dark it is (0 daylight - 1 the cave's dark): scales the light players carry outdoors.
    const darkness = 1 - (((ambient >> 16) & 255) + ((ambient >> 8) & 255) + (ambient & 255)) / (3 * 255);
    const w = app.width, h = app.height;
    if (!this.lightTexture || this.lightTexture.width !== w || this.lightTexture.height !== h) {
      this.lightTexture?.destroy(true);
      this.lightTexture = RenderTexture.create({ width: Math.max(1, w), height: Math.max(1, h) });
      this.lightSprite.texture = this.lightTexture;
    }
    const scene = this.lightScene;
    if (!this.lightBg.parent) scene.addChild(this.lightBg);
    this.lightBg.clear().rect(0, 0, w, h).fill(ambient);
    let used = 0;
    const light = (x: number, y: number, radius: number, color: number) => {
      const c = this.worldToScreen(x, y), r = radius * this.zoom;
      if (r <= 0 || c.x + r < 0 || c.y + r < 0 || c.x - r > w || c.y - r > h) return;
      let s = this.lightPool[used];
      if (!s) {
        s = new Sprite(this.lightGlow);
        s.blendMode = 'add';
        this.lightPool.push(s);
        scene.addChild(s);
      }
      used++;
      s.visible = true;
      s.tint = color;
      s.position.set(c.x - r, c.y - r);
      s.width = s.height = r * 2;
    };
    // Fires flicker: the radius wavers a little, each light out of step with the others.
    const flicker = (radius: number, seed: number) =>
      radius * (1 + 0.05 * Math.sin(this.time * 11 + seed) + 0.035 * Math.sin(this.time * 17.3 + seed * 1.7));
    // Terrain lights change with chunks, not frames. Avoid looking up every visible tile at night.
    for (const c of this.chunks.values()) for (const l of c.lights)
      light(l.x, l.y, l.flicker ? flicker(l.radius, l.seed) : l.radius, l.color);
    for (const p of this.portals) if (p.sprite.visible) light(p.sprite.x + 8, p.sprite.y + 10, 36, 0xaa78ff);
    for (const l of this.shotLights) light(l.x, l.y, flicker(l.radius, l.x * 0.21), l.color);
    for (const l of this.zoneLights) light(l.x, l.y, l.radius, l.color);
    for (const l of this.resourceLights) light(l.x, l.y, l.flicker ? flicker(l.radius, l.seed) : l.radius, l.color);
    for (const l of this.mapLights) light(l.x, l.y, l.flicker ? flicker(l.radius, l.x * 0.13 + l.y * 0.07) : l.radius, l.color);
    // Players carry a light: in caves a lantern's worth, outdoors at night a faint glow.
    for (const [key, c] of this.characters) {
      const me = key === 'me';
      if (this.dark) light(c.root.x, c.root.y - 8, me ? 110 : 64, 0xffd6a0);
      else if (darkness > 0.3) light(c.root.x, c.root.y - 8, me ? 56 : 36, me ? 0x8a7458 : 0x5a4c3a);
      if (c.burning) light(c.root.x, c.root.y - 6, flicker(40, c.root.x * 0.3), 0xff8a3c);
    }
    for (let i = used; i < this.lightPool.length; i++) this.lightPool[i].visible = false;
    app.pixi.renderer.render({ container: scene, target: this.lightTexture, clear: true });
  }
}

function add(parent: Container, texture: Texture, lx: number, ly: number): void {
  const s = new Sprite(texture);
  s.position.set(lx * TS, ly * TS);
  parent.addChild(s);
}

/** Soft radial falloff in bands, for the light map. */
function makeGlow(): Texture {
  const size = 128, c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const img = g.createImageData(size, size);
  const r = size / 2;
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const d = Math.hypot((x + 0.5 - r) / r, (y + 0.5 - r) / r);
      let v = Math.max(0, Math.min(1, 1 - d));
      v = v * v * (3 - 2 * v);
      v = Math.round(v * 6) / 6;
      const i = (y * size + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = 255;
      img.data[i + 3] = Math.round(v * 255);
    }
  g.putImageData(img, 0, 0);
  return Texture.from(c);
}

export { CELL };
